/**
 * awaitAuthReady
 *
 * Resolves once Firebase Auth has delivered a non-null user via
 * `onIdTokenChanged`. Used as a defensive gate before issuing Cloud
 * Functions callables on route changes — without it, a callable can fire
 * during a token-refresh window and receive a 401 Unauthenticated, which
 * Firebase normally retries but produces noisy console errors and a
 * perceptible delay (SYD-1925 #06).
 *
 * If a user is already attached, resolves on the next microtask. The
 * listener auto-unsubscribes after first non-null delivery, so the cost
 * per call is one onIdTokenChanged registration.
 */
import { onIdTokenChanged, type User } from 'firebase/auth';

export const awaitAuthReady = async (): Promise<void> => {
  // Lazy import: keeps `startAuth`'s `getAuth(app)` side effect out of the
  // module-init graph for callers that only re-export this function. Tests
  // that mock the callsite (e.g., `fetchRecordsList`) don't need to also
  // mock `startAuth` just to satisfy the import chain.
  const { auth } = await import('@/services/startAuth');
  return new Promise<void>((resolve) => {
    const unsubscribe = onIdTokenChanged(auth, (user: User | null) => {
      if (user) {
        unsubscribe();
        resolve();
      }
    });
  });
};
